import React, { useContext, useEffect, useState, forwardRef } from 'react';
import styled from 'styled-components';
import { flatten, uniqBy, chunk } from 'lodash-es';
import { FormattedMessage } from 'react-intl';
import { Grid } from '@material-ui/core';
import { formatDateMMDDYYYY } from '@mummssoftware/utils/formatters';
import { parseDateMMDDYYYYSlashes } from '@mummssoftware/utils/dates';
import { PNCounterContext } from '@mummssoftware/global-context/PNCounter';
import { ENTITY_MEDICATION } from '@mummssoftware/global-context/PNCounter/constants';
import { WidgetPrintHeader, NoRecordsPrintDiv } from '@mummssoftware/common-ui';
import {
  getActiveSeriesMeds,
  getFullyDiscontinuedMeds,
  orderingPhysicianLabel,
  verbalReceived,
  formatDate,
  makeActiveIdHash,
  sortMeds,
} from '@mummssoftware/utils/medications/utils';
import { showPrintNotes } from '@mummssoftware/utils/utils';
import { safariDetect } from '@mummssoftware/utils/web';
import { CertGlobalContext } from '@mummssoftware/global-context';
import { DashboardCoverPage } from '@mummssoftware/common-ui/PagedJS';
import { PrintNote } from './PrintNote';
import { messages } from '../../utils/messages';
import defaults from '../../../translations/en.json';

const isSafari = safariDetect();

const DivSpacedLine = styled.div`
  display: block;
  justify-content: space-between;
  margin-top: 30px;
  margin-left: -50px;
`;

const SignatureLine = styled.div`
  list-style-type: none;
  padding-bottom: 5px;
`;

const PharmacistSigLine = styled.div`
  list-style-type: none;
  width: 100%;
  padding-bottom: 5px;
  display: flex;
  justify-content: right;
`;

const StyledApprovalStatement = styled.div`
  margin-top: 30px;
  border: 1px solid;
  padding: 20px;
  display: block;
  border-color: white;
  color: ${(props) => props.theme.palette.text.primary};
  @media print {
    color: ${(props) => props.theme.palette.common.black};
    border-color: ${(props) => props.theme.palette.common.black};
  }
`;
const StyledConfirmationStatement = styled.div`
  margin-top: 30px;
  padding: 10px;
  display: block;
  font-weight: bold;
  font-size: 1.25em;
  color: ${(props) => props.theme.palette.text.primary};
  @media print {
    color: ${(props) => props.theme.palette.common.black};
    border-color: ${(props) => props.theme.palette.common.black};
  }
`;

// const BlockLine = styled.div`
//   margin-top: 1px;
//   border: 1px solid white;
//   padding: 5px;
// `;

const StatementDiv = styled.div`
  @media print {
    display: block;
    visibility: visible;
    page-break-after: always;
  }
`;
const EmptyPrintDiv = styled.div`
  @media print {
    page-break-after: always;
  }
`;
const PrintHeader = styled.th`
  font-weight: 500;
`;
// display none if not printing
const PrintContainer = styled.div`
  ${(props) => (props.isPrintMode ? '' : 'display: none;')}
  overflow: visible;
  @media print {
    @page {
      size: landscape;
      margin: 5mm 0mm;
    }
    display: block;
    color: ${(props) => props.theme.palette.common.black} !important;
    background-color: #ffffff;
    overflow: visible;
    visibility: visible;
    body {
      /* this affects the margin on the content before sending to printer */
      margin: 0px;
      -webkit-print-color-adjust: exact;
    }
    div {
      page-break-inside: avoid;
    }
    table {
      page-break-after: auto;
    }
    tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    td {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    thead {
      display: table-header-group;
    }
    tfoot {
      display: table-footer-group;
    }
  }
`;

// the color is white for the print interface
// the color is black when printing
const PrintTable = styled.table`
  width: 100%;
  display: table;
  visibility: visible;
  font-size: ${isSafari ? '.08in' : '10px'};
  @media print {
    font-family: 'auto', sans-serif;
    color: ${(props) => props.theme.palette.common.black};
  }
`;

const Header = styled.thead`
  display: table-header-group;
`;

const HeaderGrid = styled(Grid)`
  margin-top: 10px;
  padding: 0 6px;
  font-size: 14px;
  text-decoration: underline;
  color: ${(props) => props.theme.palette.text.primary};
  @media print {
    color: ${(props) => props.theme.palette.common.black};
  }
`;
// todo common-ui
const PrintDiv = styled.div`
  display: flex;
  flex-direction: column;
`;

const MedicationsContains = styled.tbody``;

const Medication = styled.li`
  page-break-inside: avoid;
  list-style-type: none;
  padding: 5px;
  border-bottom: 1px solid lightgray;
  color: ${(props) => props.theme.palette.text.primary};
  @media print {
    color: ${(props) => props.theme.palette.common.black};
  }
`;

const Details = styled.div`
  margin: 0;
  margin-top: 8px;
  width: 75%;
  font-style: italic;
  font-size: ${isSafari ? '.06in' : '12px'};
  color: ${(props) => props.theme.palette.text.secondary};
  @media print {
    color: ${(props) => props.theme.palette.common.black};
  }
`;
// h3 cannot appear as a child of <tbody> - FIX
const StyledHeader = styled.h3`
  text-align: center;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
  font-weight: bold;
  margin-bottom: 15px;
  color: ${(props) => props.theme.palette.text.primary};
  @media print {
    color: ${(props) => props.theme.palette.common.black};
  }
`;

export const LandscapeOrientation = () => (
  <style type="text/css">
    {'@media print{@page {size: landscape; margin: 5mm 0mm;}}'}
  </style>
);

const MedHeaders = () => (
  <HeaderGrid direction="row" align="left" container className="header-grid">
    <Grid xs={5} align="left" container item className="grid-container-xs-5">
      <Grid item className="grid-item">
        <FormattedMessage {...messages.name} />
      </Grid>
    </Grid>
    <Grid xs={7} align="left" container item className="grid-container-xs-7">
      <Grid xs={2} item className="grid-item-xs-2">
        <FormattedMessage {...messages.quantity} />
      </Grid>
      <Grid xs={3} item className="grid-item-xs-3">
        <FormattedMessage {...messages.physician} />
      </Grid>
      <Grid xs={3} item className="grid-item-xs-3">
        <FormattedMessage {...messages.verbalCol} />
      </Grid>
      <Grid xs={1} item className="grid-item-xs-1">
        <FormattedMessage {...messages.covered} />
      </Grid>
      <Grid xs={3} item className="grid-item-xs-3">
        <FormattedMessage {...messages.date} />
      </Grid>
    </Grid>
  </HeaderGrid>
);

const MedicationName = styled.span`
  font-size: ${isSafari ? '.10in' : '14px'};
  font-weight: bold;
`;

// RIBBON Widget: Include all Electronic boxes for each physician that signed electronically, no duplicates, within the date range
// MEDS Widget: Include only the LAST Electronic box for each physician that signed electronically.
const buildElectronicSigList = (allmeds, config) => {
  const flags = {};
  const sigList = allmeds
    .map((m) => ({
      reconciledphysicianfirstname: m.reconciledphysicianfirstname,
      reconciledphysicianid: m.reconciledphysicianid,
      reconciledphysicianlastname: m.reconciledphysicianlastname,
      reconciledsource: m.reconciledsource,
      reconcileduserfirstname: m.reconcileduserfirstname,
      reconcileduserid: m.reconcileduserid,
      reconcileduserlastname: m.reconcileduserlastname,
      reconcileddate: m.reconcileddate,
    }))
    .filter((m) => {
      if (
        flags[m.reconcileddate] ||
        !m.reconcileddate ||
        !m.reconciledphysicianid ||
        !m.reconciledphysicianfirstname ||
        !m.reconciledphysicianlastname
      ) {
        return false;
      }
      flags[m.reconcileddate] = true;
      return true;
    });

  const list = sigList.reduce((seed, med) => {
    if (seed[med.reconciledphysicianid]) {
      seed[med.reconciledphysicianid].push(med);
    } else {
      seed[med.reconciledphysicianid] = [med];
    }
    return seed;
  }, {});
  const uniqList = Object.values(list).map((listitem) =>
    uniqBy(listitem, (x) => formatDateMMDDYYYY(x.reconcileddate)),
  );
  const orderedList = uniqList.map((listitem) =>
    listitem.sort(
      (a, b) => new Date(b.reconcileddate) - new Date(a.reconcileddate),
    ),
  );
  if (config) {
    return flatten(Object.values(orderedList));
  }
  const lastItem = orderedList.map((listitem) => listitem[0]);
  return flatten(Object.values(lastItem));
};

const medsNotReconciled = (meds) => meds.filter((m) => !m.reconcileddate);

const getCoveredLabel = (covered) => {
  switch (covered) {
    case 'true':
      return <FormattedMessage {...messages.coveredYes} />;
    case 'false':
      return <FormattedMessage {...messages.coveredNo} />;
    default:
      return null;
  }
};

export const PrintLayout = forwardRef((props, ref) => {
  const {
    medications,
    patient,
    facility,
    hospiceName,
    widgetTitle,
    isPrintMode,
    printConfig,
    agency,
    observation,
    onWidgetReady,
  } = props;
  const { countValues } = useContext(PNCounterContext);
  const [cppNotes, setCPPnotes] = useState(null);

  const medNotes =
    countValues &&
    patient &&
    countValues[patient.patientNumber][ENTITY_MEDICATION];

  const showNotes = showPrintNotes(printConfig);

  // on Mount
  useEffect(() => {
    // Include this so React won't complain about making this a
    // functional component.
    // callback to alert dashboard the view is ready
    setTimeout(() => {
      onWidgetReady && onWidgetReady('ClearScripts');
    }, 2000);
  }, []);

  useEffect(() => {
    // make API calls when needed
    // => only when printing
    // => only if meds Notes
    // refactor with Global Context
    if (showNotes && medNotes && medNotes.length) {
      getNotes();
    }
  }, [JSON.stringify(medNotes)]);

  const getNotes = async () => {
    const pnNotes =
      observation &&
      (await observation.getWidgetPnNotes(
        patient?.patientNumber,
        agency,
        ENTITY_MEDICATION,
      ));

    setCPPnotes(pnNotes);

    // @note save in global context but refactor on PN Context coming
  };

  const isIdgMeeting = printConfig && printConfig.idg;

  const { patientCertHistory } = useContext(CertGlobalContext);

  const medsToPrint = !!medications.length;

  const activeHash = makeActiveIdHash(medications);
  const activeSeriesMeds = getActiveSeriesMeds(medications, activeHash);
  const discontinuedMeds = getFullyDiscontinuedMeds(medications, activeHash);

  const chunkedActiveMeds = chunk(activeSeriesMeds.sort(sortMeds), 15);
  const chunkedInactiveMeds = chunk(discontinuedMeds.sort(sortMeds), 15);

  const renderChunkedMeds = (meds) => (
    <>
      {meds.map((medsarray, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <tr key={`print-${i}`}>
          <PrintHeader
            // eslint-disable-next-line react/no-array-index-key
            key={`print-${i}`}
            className="meds-print-header"
          >
            {medsarray.map((medication, ind) => (
              <Medication
                // eslint-disable-next-line react/no-array-index-key
                key={`print--${ind}-${medication.id}`}
                className="meds-medication-line"
              >
                <Grid
                  direction="row"
                  justifyContent="space-between"
                  align="left"
                  container
                  className="grid-container-space-between"
                >
                  <Grid
                    xs={5}
                    align="left"
                    container
                    item
                    className="grid-container-xs-5"
                  >
                    <Grid item>
                      <MedicationName>{`${medication.drugdescription}`}</MedicationName>
                    </Grid>
                  </Grid>
                  <Grid
                    xs={7}
                    align="left"
                    container
                    item
                    className="grid-container-xs-7"
                  >
                    <Grid xs={2} item className="grid-item-xs-2">
                      {Math.trunc(medication.quantity)}
                    </Grid>
                    <Grid xs={3} item className="grid-item-xs-3">
                      {medication.orderingphysicianid &&
                        orderingPhysicianLabel(medication)}
                    </Grid>
                    <Grid xs={3} item className="grid-item-xs-3">
                      {verbalReceived(medication)}
                    </Grid>
                    <Grid xs={1} item className="grid-item-xs-1">
                      {getCoveredLabel(medication.coveredbyhospice)}
                    </Grid>
                    <Grid xs={3} item className="grid-item-xs-3">
                      {medication.startdate_shifted
                        ? formatDate(
                            parseDateMMDDYYYYSlashes(
                              medication.startdate_shifted,
                            ),
                          )
                        : ''}

                      {medication.stopdate_shifted
                        ? ` - ${formatDate(
                            parseDateMMDDYYYYSlashes(
                              medication.stopdate_shifted,
                            ),
                          )}`
                        : ''}
                    </Grid>
                  </Grid>
                </Grid>
                {medication.sig ? (
                  <Details className="meds-medication-details">
                    {medication.sig}
                  </Details>
                ) : null}
                {medication.coveredbyhospice === 'false' &&
                medication.notcoveredreason ? (
                  <Details className="meds-medication-details">
                    <u>Not Covered:</u> {medication.notcoveredreason}
                  </Details>
                ) : null}
                {cppNotes && cppNotes[medication.externalid] ? (
                  <PrintNote notes={cppNotes[medication.externalid]} />
                ) : null}
              </Medication>
            ))}
          </PrintHeader>
        </tr>
      ))}
    </>
  );

  return (
    <PrintDiv id="meds-print-layout" className="meds-print-layout" ref={ref}>
      {widgetTitle ? (
        <WidgetPrintHeader title={widgetTitle} />
      ) : (
        <DashboardCoverPage
          patient={patient}
          patientCertHistory={patientCertHistory}
        />
      )}
      <PrintContainer isPrintMode={isPrintMode}>
        {medsToPrint ? (
          <PrintTable className="meds-print-table">
            <Header className="meds-header">
              <tr>
                <th>
                  <StyledHeader className="meds-styled-header">
                    {`${patient.firstName} ${patient.lastName}`} - {hospiceName}{' '}
                    - {facility}
                  </StyledHeader>
                  <MedHeaders />
                </th>
              </tr>
            </Header>
            <LandscapeOrientation></LandscapeOrientation>
            <MedicationsContains>
              <StyledHeader
                className="meds-styled-header"
                style={{
                  width: '100%',
                  textAlign: 'left',
                  marginLeft: '5px',
                }}
              >
                {defaults.ACTIVE_MEDICATIONS}
              </StyledHeader>
              {renderChunkedMeds(chunkedActiveMeds)}
              <StyledHeader
                className="meds-styled-header"
                style={{
                  width: '100%',
                  textAlign: 'left',
                  marginLeft: '5px',
                }}
              >
                {defaults.DISCONTINUED_MEDICATIONS}
              </StyledHeader>
              {renderChunkedMeds(chunkedInactiveMeds)}
            </MedicationsContains>
            {/* only show boxes if NOT IDG MEETING */}
            <tfoot>
              <tr>
                {isIdgMeeting ? (
                  <StyledConfirmationStatement>
                    <FormattedMessage {...messages.medStatement} />
                  </StyledConfirmationStatement>
                ) : (
                  <PrintHeader>
                    {/* only show  blank Manual Signature boxes when printing from Meds sheet */}
                    {/* only show if there is a physician listed that does not have login credentials */}
                    {/* <BlockLine /> */}
                    {/* <BlockLine /> */}
                    {!printConfig && medsNotReconciled(medications).length ? (
                      <StyledApprovalStatement>
                        <FormattedMessage {...messages.physApprovalState} />
                        <Grid
                          direction="row"
                          justifyContent="flex-end"
                          container
                        >
                          <Grid xs={4} item>
                            <DivSpacedLine>
                              <SignatureLine>
                                X___________________________Date___________
                              </SignatureLine>
                            </DivSpacedLine>
                          </Grid>
                        </Grid>
                      </StyledApprovalStatement>
                    ) : null}
                    <StatementDiv>
                      {printConfig ? null : (
                        <StyledApprovalStatement>
                          <FormattedMessage {...messages.pharmStatement} />
                          <Grid
                            direction="row"
                            justifyContent="flex-end"
                            container
                          >
                            <Grid xs={4} item>
                              <DivSpacedLine>
                                <PharmacistSigLine>
                                  <p>
                                    Reviewed By:
                                    Name___________________________Title___________X___________________________Date___________
                                  </p>
                                </PharmacistSigLine>
                              </DivSpacedLine>
                            </Grid>
                          </Grid>
                        </StyledApprovalStatement>
                      )}
                      {buildElectronicSigList(medications, printConfig).map(
                        (m, i) => (
                          <StyledApprovalStatement
                            // eslint-disable-next-line react/no-array-index-key
                            key={`signed-${i}-${m.id}-${m.lastupdatedate}`}
                          >
                            <FormattedMessage
                              {...messages.reconcileAck}
                              values={{
                                doctor: `${m.reconciledphysicianlastname}, ${m.reconciledphysicianfirstname}`,
                                date: m.reconcileddate.split(' ')[0],
                              }}
                            />
                          </StyledApprovalStatement>
                        ),
                      )}
                    </StatementDiv>
                    {showNotes && <EmptyPrintDiv />}

                    <StyledConfirmationStatement>
                      <FormattedMessage {...messages.medStatement} />
                    </StyledConfirmationStatement>
                  </PrintHeader>
                )}
              </tr>
            </tfoot>
          </PrintTable>
        ) : (
          <NoRecordsPrintDiv>
            <p>{defaults.noMeds}</p>
          </NoRecordsPrintDiv>
        )}
      </PrintContainer>
    </PrintDiv>
  );
});

export default PrintLayout;
